import { FormEvent, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { getTranslationKey } from 'helpers/texting';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  resetLoginWithPasswordFlow,
  resetOtpLoginFlow,
  setCreateAccountWithPasswordFlowStatus,
  setLoginFlowStatus,
  setOtpLoginFlowStatus
} from 'modules/ScrShopNew/store/slices/shop';
import {
  loginCustomerWithPassword,
  requestNewPassword,
  sendOtpLoginCode,
  startOtpLogin,
  verifyLoginCode
} from 'modules/ScrShopNew/store/slices/shop/actions';
import {
  loginFlowStatusSelector,
  otpLoginFlowSelector,
  signupWithPasswordFlowStatusSelector
} from 'modules/ScrShopNew/store/slices/shop/selectors';
import type { ISignedInCustomer, OtpLoginStep } from 'modules/ScrShopNew/store/slices/shop/types';

const T = 'otpLogin';

export type OtpFieldKind = 'email' | 'code' | 'password';

export interface IOtpLoginLink {
  label: string;
  onClick: () => void;
  dataCy: string;
}

interface IOptions {
  // runs once a code or password sign-in went through, e.g. for the shop's checkout steps
  onSignedIn?: (signedIn: ISignedInCustomer) => void;
}

export const OTP_INPUT_PROPS: Record<OtpFieldKind, Record<string, unknown>> = {
  email: { type: 'email', autoComplete: 'email' },
  code: { type: 'text', inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 12 },
  password: { type: 'password', autoComplete: 'current-password', withEye: true }
};

const FIELD_BY_STEP: Record<OtpLoginStep, OtpFieldKind | null> = {
  email: 'email',
  code: 'code',
  registerCode: 'code',
  password: 'password',
  newPassword: 'password',
  forgotConfirm: null
};

const emailSchema = Yup.string().trim().email().required();

const getSubmitLabel = (step: OtpLoginStep) => {
  switch (step) {
    case 'email':
      return getTranslationKey(`${T}.continue`);
    case 'registerCode':
      return getTranslationKey(`${T}.signInOrCreate`);
    case 'forgotConfirm':
      return getTranslationKey(`${T}.requestNewPassword`);
    default:
      return getTranslationKey(`${T}.signIn`);
  }
};

const getDescription = (step: OtpLoginStep, email: string) => {
  switch (step) {
    case 'code':
    case 'registerCode':
      return getTranslationKey(`${T}.codeSent`);
    case 'forgotConfirm':
      return getTranslationKey(`${T}.requestNewPasswordText`, { email });
    case 'newPassword':
      return getTranslationKey(`${T}.newPasswordSent`);
    default:
      return undefined;
  }
};

// Screens 3 and 6 share the password field; only screen 3 labels it (screen 6 has a description).
const getFieldLabel = (step: OtpLoginStep, field: OtpFieldKind | null) => {
  if (field === 'email') return getTranslationKey(`${T}.emailLabel`);
  if (step === 'password') return getTranslationKey(`${T}.passwordLabel`);

  return undefined;
};

const getPlaceholder = (field: OtpFieldKind) => {
  if (field === 'email') return getTranslationKey(`${T}.emailLabel`);
  if (field === 'code') return getTranslationKey(`${T}.codeLabel`);

  return '******';
};

// One-time-code sign-in / registration, shared by the gallery and the shop modal: only the
// presentation differs, the steps, thunks and requests are the same.
export const useOtpLoginFlow = ({ onSignedIn }: IOptions = {}) => {
  const dispatch = useAppDispatch();
  const { showLoginWithPasswordModal: isOpen, passwordIsInvalid } =
    useAppSelector(loginFlowStatusSelector);
  const { step, email, hasPassword, codeIsInvalid, isLoading } =
    useAppSelector(otpLoginFlowSelector);
  const { showEmailModal: isSignupRequested } = useAppSelector(
    signupWithPasswordFlowStatusSelector
  );

  const [emailValue, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const field = FIELD_BY_STEP[step];

  // every screen starts with an empty secret field
  useEffect(() => {
    setCode('');
    setPassword('');
  }, [step]);

  useEffect(() => {
    if (isOpen) return;

    setEmailValue('');
    setEmailError('');
  }, [isOpen]);

  // there is no separate sign-up any more: a sign-up request opens the shared email screen
  useEffect(() => {
    if (!isSignupRequested) return;

    dispatch(setCreateAccountWithPasswordFlowStatus({ showEmailModal: false }));
    dispatch(setLoginFlowStatus({ showLoginWithPasswordModal: true }));
  }, [isSignupRequested]);

  const close = () => {
    dispatch(resetLoginWithPasswordFlow());
    dispatch(resetOtpLoginFlow());
  };

  const goToStep = (nextStep: OtpLoginStep) => {
    dispatch(setOtpLoginFlowStatus({ step: nextStep, codeIsInvalid: false }));
    dispatch(setLoginFlowStatus({ passwordIsInvalid: false }));
  };

  const submitEmail = () => {
    if (!emailSchema.isValidSync(emailValue)) {
      setEmailError(getTranslationKey('checkEmailFormat'));

      return;
    }

    setEmailError('');
    dispatch(startOtpLogin(emailValue));
  };

  const signIn = async (
    thunk: ReturnType<typeof verifyLoginCode> | ReturnType<typeof loginCustomerWithPassword>
  ) => {
    const action: { payload?: unknown } = await dispatch(thunk as any);
    const signedIn = action.payload as ISignedInCustomer | undefined;

    if (signedIn?.endCustomer) onSignedIn?.(signedIn);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading) return;

    if (field === 'email') submitEmail();
    else if (field === 'code') signIn(verifyLoginCode(code));
    else if (field === 'password') signIn(loginCustomerWithPassword({ email, password }));
    else dispatch(requestNewPassword());
  };

  const values: Record<OtpFieldKind, string> = { email: emailValue, code, password };
  const setters: Record<OtpFieldKind, (value: string) => void> = {
    email: setEmailValue,
    code: setCode,
    password: setPassword
  };

  // the email step sends no code to an account with a password, so that one asks /otp/send
  const sendCode = () => {
    if (isLoading) return;

    setCode('');
    dispatch(hasPassword ? sendOtpLoginCode() : startOtpLogin(email));
  };

  const getLinks = (): IOtpLoginLink[] => {
    const links: IOtpLoginLink[] = [];

    if (field === 'code' && codeIsInvalid) {
      links.push({
        label: getTranslationKey(`${T}.resendCode`),
        onClick: sendCode,
        dataCy: 'otp-login-resend-code'
      });
    }

    // an account with a password starts on it; from the code screen it leads back there
    if (step === 'code' && hasPassword) {
      links.push({
        label: getTranslationKey(`${T}.signInWithPassword`),
        onClick: () => goToStep('password'),
        dataCy: 'otp-login-sign-in-with-password'
      });
    }

    // no code was sent for this account yet: asking for one moves on to the code screen
    if (step === 'password') {
      links.push(
        {
          label: getTranslationKey(`${T}.signInWithCode`),
          onClick: sendCode,
          dataCy: 'otp-login-sign-in-with-code'
        },
        {
          label: getTranslationKey(`${T}.forgotPassword`),
          onClick: () => goToStep('forgotConfirm'),
          dataCy: 'otp-login-forgot-password'
        }
      );
    }

    return links;
  };

  const getNotice = (): string | false => {
    if (field === 'code' && codeIsInvalid) return String(getTranslationKey(`${T}.codeInvalid`));
    if (field === 'password' && passwordIsInvalid) {
      return String(getTranslationKey('enterPasswordModal.passwordIsInvalid'));
    }

    return false;
  };

  return {
    isOpen,
    close,
    step,
    title: getTranslationKey(`${T}.title`),
    description: getDescription(step, email),
    notice: getNotice(),
    submitLabel: getSubmitLabel(step),
    submitDisabled: isLoading || (!!field && !values[field].trim()),
    links: getLinks(),
    // the mockups put the alternatives between the field and the button
    linksPlacement: 'aboveSubmit' as const,
    onSubmit,
    field: field && {
      kind: field,
      label: getFieldLabel(step, field),
      placeholder: getPlaceholder(field),
      value: values[field],
      onChange: setters[field],
      errorText: field === 'email' ? emailError : ''
    }
  };
};
